import '../index.scss';
import { SingleChoice } from '../../../../../../components/Icons/SingleChoice';
import { ExplanationBanner } from '../../../../../../components/Icons/ExplanationBanner';
import { Button, Checkbox, Divider, Form, message, Modal, Switch, Tooltip } from 'antd';
import { FloatInput } from '../../../../../../components/Fields/FloatInput';
import { DndContext, pointerWithin, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import type { DragCancelEvent, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DraggableItem } from './components/DraggableItem';
import { SortableFormItem, SortableFormItemDragOverlay } from './components/SortableFormItem';
import { DroppableArea } from './components/DroppableArea';
import { SupplierQuestionaryFormIcon } from '../../../../../../components/Icons/SupplierQuestionaryFormIcon';
import { AddSectionIcon } from '../../../../../../components/Icons/AddSectionIcon';
import BGImage from '../../../../../../assets/BG.png';
import Plus from '../../../../../../assets/Plus, Add.svg';
import { useLazyGetBuilderComponentsQuery, useLazyShowBuilderDataQuery } from '../../../../../../api/expenseAreas';
import { FormItem, ParentFieldI } from '../../../../../../api/types/expenseareas';
import { FloatTextArea } from '../../../../../../components/Fields/FloatTextArea';
import Delete from '../../../../../../components/Icons/Delete';
import { CustomMultiSelect } from '../../../../../../components/Fields/CustomMultiSelect';
import { Loader } from '../../../../../../components/Loader/Loader';
import ArrowDown from '../../../../../../components/Icons/ArrowDown';
import ArrowDownDown from '../../../../../../components/Icons/ArrowDownDown';
import i from '../../../../../../assets/i.svg';
import useScoreCardController from './ScoreCard.controller';
import { Slider } from '../../../../../../components/Icons/Slider';
import { ShortAnswerIcon } from '../../../../../../components/Icons/ShortAnswerIcon';
import { Paragraph } from '../../../../../../components/Icons/Paragraph';
import { MultipleChoice } from '../../../../../../components/Icons/MultipleChoice';
import { DropdownIcon } from '../../../../../../components/Icons/DropdownIcon';
import arrow from '../../../../../../assets/arrow.svg';
import {
    getFirstBuilderValidationErrorElement,
    isFieldIdInErrors,
    scrollIntoFormColumnSmooth
} from '../builderFormUtils';
import { useAdminFormPreview } from '../../useAdminFormPreview';

const iconsMap: Record<string, (fill?: string) => JSX.Element> = {
    slider: (fill) => <Slider fill={fill} />,
    'single-select': (fill) => <SingleChoice fill={fill} />,
    'explanation-banner': (fill) => <ExplanationBanner fill={fill} />
};

const iconsMapForParentFields: Record<string, (fill?: string) => JSX.Element> = {
    'Short answer': (fill) => <ShortAnswerIcon fill={fill} />,
    Paragraph: (fill) => <Paragraph fill={fill} />,
    'Single select': (fill) => <SingleChoice fill={fill} />,
    'Multiple choice': (fill) => <MultipleChoice fill={fill} />,
    Dropdown: (fill) => <DropdownIcon fill={fill} />,
    'Explanation banner': (fill) => <ExplanationBanner fill={fill} />
};

export const ScoreCardBuilder = () => {
    const {
        publish,
        saveAsDraftFunc,
        isSuccessSaveAsDraft,
        isLoadingDraft,
        data,
        setData,
        isCantPublishModal,
        setIsCantPublishModal,
        sectionsError,
        setSectionsError,
        fieldsError,
        setFieldsError,
        isSuccessPublishForm,
        ExpenseAreaId,
        isLoadingPublished,
        addNewSection,
        selectedElement,
        setSelectedElement,
        updateFieldOrSection,
        updateSettingField,
        updateOptionField,
        addOption,
        selectAllOptions,
        deselectAllOptions,
        removeOption,
        removeItem,
        removeSection,
        findItem,
        maxSwitchValue,
        moveSectionToStart,
        moveSectionToEnd,
        moveSectionDown,
        moveSectionUp,
        updateOptionMultiSelectedInSetting,
        setComponents
    } = useScoreCardController();

    const { openPreview, isOpeningPreview, previewModal } = useAdminFormPreview('score-card', ExpenseAreaId);

    const [fields, setFields] = useState<any>([]);
    const [expandedSections, setExpandedSections] = useState<Record<string | number, boolean>>({});

    const toggleSection = (id: string | number) => {
        setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const [isDeleteOption, setIsDeleteOption] = useState<null | number>(null);
    const [isDeleteItem, setIsDeleteItem] = useState<boolean>(false);
    const [isDeleteSection, setIsDeleteSection] = useState<boolean>(false);

    const [activeItem, setActiveItem] = useState<any | null>(null);

    const formScrollRef = useRef<HTMLDivElement>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 }
        })
    );
    const [commonScorePoints, setCommonScorePoints] = useState(0);

    const [getBuilderComponent, { data: componentsData }] = useLazyGetBuilderComponentsQuery();

    const [showBuilderData, { data: FormData }] = useLazyShowBuilderDataQuery();

    useEffect(() => {
        let score = 0;

        data?.sections?.forEach((section) => {
            section.fields.forEach((field) => {
                if (field?.type === 'slider') {
                    score = score + Math.max(0, ...(field?.options?.map((elem) => Number(elem.rating) || 0) || []));
                }
            });
        });

        setCommonScorePoints(score);
    }, [data]);

    useEffect(() => {
        if (!componentsData) return;
        setComponents(componentsData);
    }, [componentsData]);

    useEffect(() => {
        let sectionErrors: string[] = [...sectionsError];
        let sectionFieldsErrors: string[] = [...fieldsError];
        data?.sections?.forEach((section) => {
            if (section.title && section.fields?.length) {
                sectionErrors = sectionErrors.filter((id) => String(id) !== String(section?.localId));
            }

            section?.fields.forEach((field) => {
                const type = field.type;

                if (type === 'single-select' || type === 'slider') {
                    if (field.label && field?.options?.every((option) => option.value)) {
                        sectionFieldsErrors = sectionFieldsErrors.filter((id) => String(id) !== String(field?.id));
                    }
                }
            });
        });
        setSectionsError(sectionErrors);
        setFieldsError(sectionFieldsErrors);
    }, [data]);

    const showFormData = () => {
        if (ExpenseAreaId) {
            const baseData = {
                id: ExpenseAreaId,
                type: 'score-card'
            };
            getBuilderComponent(baseData);
            showBuilderData(baseData);
        }
    };

    useEffect(() => {
        if (isSuccessSaveAsDraft) {
            message?.success('Your form has been saved as a draft');
            showFormData();
        }
    }, [isSuccessSaveAsDraft]);

    useEffect(() => {
        if (isSuccessPublishForm) {
            message?.success('Your form has been published');
            showFormData();
        }
    }, [isSuccessPublishForm]);

    useEffect(() => {
        if (FormData) {
            const sectionLocalId = FormData?.data?.sections?.map((section) => ({
                ...section,
                localId: section.id
            }));
            setData(
                structuredClone({
                    ...FormData?.data,
                    sections: sectionLocalId
                })
            );
        }
    }, [FormData, isSuccessPublishForm, isSuccessSaveAsDraft]);

    const selectedElementData = useMemo(() => {
        if (!selectedElement) return null;
        return (
            data?.sections?.flatMap((elem) => elem?.fields)?.find((item) => item?.id + '' === selectedElement) || null
        );
    }, [selectedElement, data]);

    useEffect(() => {
        if (componentsData) {
            const fields = componentsData?.data?.fields.map((elem: FormItem) => ({
                ...elem,
                id: elem.type
            }));
            setFields(fields);
        }
    }, [componentsData, data]);

    const queueScrollToFirstValidationError = (sectionErrors: string[], fieldsErrors: string[]) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const container = formScrollRef.current;
                if (!container) return;
                const el = getFirstBuilderValidationErrorElement(container, data?.sections, sectionErrors, fieldsErrors);
                scrollIntoFormColumnSmooth(container, el);
            });
        });
    };

    const handlePublishClick = () => {
        const result = publish();
        if (!result.ok && result.reason === 'invalid') {
            queueScrollToFirstValidationError(result.sectionErrors, result.fieldsErrors);
        }
    };

    useEffect(() => {
        showFormData();
    }, [ExpenseAreaId]);

    const handleDragCancel = (_event: DragCancelEvent) => {
        document.body.classList.remove('dragging');
        setActiveItem(null);
    };

    const handleDragStart = (event: DragStartEvent) => {
        document.body.classList.add('dragging');
        const { active } = event;
        const id = active.id.toString();

        setSelectedElement(id);
        const fromPalette = fields.find((el: any) => el.type === id);
        if (fromPalette) {
            setActiveItem({ ...fromPalette, origin: 'palette' });
            return;
        }
        if (!data?.sections) return;
        for (const section of data?.sections) {
            const found = section.fields.find((item) => item.id === id);
            if (found) {
                setActiveItem({ ...found, origin: 'form' });
                return;
            }
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        document.body.classList.remove('dragging');

        const { active, over } = event;

        try {
            if (!over) return;

            const activeId = active.id;

        const fromPalette = fields.find((el: any) => el.id === activeId);

        let sourceSectionIndex = -1;
        let destinationSectionIndex = -1;
        let activeItem: FormItem | null = null;

        data?.sections?.forEach((section, i) => {
            if (section?.fields?.find((item) => item.id === activeId)) {
                sourceSectionIndex = i;
                activeItem = section?.fields?.find((item) => item?.id === activeId)!;
            }
            if (section.id === over?.id || section.fields.find((item) => item.id === over?.id)) {
                destinationSectionIndex = i;
            }
        });

        if (fromPalette && destinationSectionIndex !== -1) {
            const base = fromPalette;
            const localId = `${base.id}-${Date.now()}`;
            setSelectedElement(localId);

            const newItem: FormItem = {
                ...base,
                id: localId
            };

            setData((prev) => {
                if (!prev) return prev;

                const updatedSections = [...prev.sections];
                updatedSections[destinationSectionIndex]?.fields?.push(newItem);

                return {
                    ...prev,
                    sections: updatedSections
                };
            });
            return;
        }

        if (activeItem && sourceSectionIndex !== -1 && destinationSectionIndex !== -1) {
            setData((prev) => {
                if (!prev) return prev;

                const updatedSections = [...prev.sections];
                const sourceItems = [...updatedSections[sourceSectionIndex].fields];
                const destItems = [...updatedSections[destinationSectionIndex].fields];

                if (sourceSectionIndex === destinationSectionIndex) {
                    const oldIndex = sourceItems.findIndex((item) => item.id === active.id);
                    const newIndex = sourceItems.findIndex((item) => item.id === over.id);
                    updatedSections[sourceSectionIndex].fields = arrayMove(sourceItems, oldIndex, newIndex);
                } else {
                    const movingItemIndex = sourceItems.findIndex((item) => item.id === active.id);
                    if (movingItemIndex === -1) return prev;

                    const movingItem = sourceItems[movingItemIndex];

                    sourceItems.splice(movingItemIndex, 1);
                    updatedSections[sourceSectionIndex].fields = sourceItems;

                    const overIndex = destItems.findIndex((item) => item.id === over.id);
                    if (overIndex === -1) {
                        destItems.push(movingItem);
                    } else {
                        destItems.splice(overIndex, 0, movingItem);
                    }
                    updatedSections[destinationSectionIndex].fields = destItems;
                }

                return {
                    ...prev,
                    sections: updatedSections
                };
            });
        }
        } finally {
            setActiveItem(null);
        }
    };

    return (
        <>
            <div className="builder">
                <div className="builder__header header">
                    <div className="header__block">
                        <div className="header__wrapper">
                            <div className="header__title">{data?.title}</div>
                            <div className="header__subtitle">Add and customize form for your needs</div>
                        </div>
                    </div>
                    <div className="header__block">
                        <div className="header__line"></div>
                        <div className="header__btn" onClick={saveAsDraftFunc}>
                            {isLoadingDraft ? <Loader color="#001965" /> : 'Save as Draft'}
                        </div>
                        <div className="header__btn" onClick={openPreview}>
                            {isOpeningPreview ? <Loader color="#001965" /> : 'Preview'}
                        </div>
                        <div className="header__btn primary" onClick={handlePublishClick}>
                            {isLoadingPublished ? <Loader color="white" /> : 'Publish'}
                        </div>
                    </div>
                </div>{' '}
                <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={handleDragStart}
                    onDragCancel={handleDragCancel}
                    onDragEnd={handleDragEnd}
                >
                    <div className="builder__main main" style={{ display: 'flex', gap: 24 }}>
                        <img
                            src={BGImage}
                            alt="bg"
                            className="bg-image"
                            onClick={(e: any) => {
                                e.stopPropagation();
                                setSelectedElement(null);
                            }}
                        />

                        {/* Левая панель */}
                        <div className="main__basic-elements basic-elements">
                            <div className="basic-elements__title kik-off">
                                <div className={`tab active`}>Basic Elements</div>{' '}
                            </div>
                            <div className="basic-elements__wrapper">
                                {fields?.map((el: any) => (
                                    <DraggableItem id={el.id} key={el.id}>
                                        {iconsMap?.[el.type]('#001965')}
                                        {el.title}
                                    </DraggableItem>
                                ))}

                                <DragOverlay>
                                    {activeItem ? (
                                        activeItem.origin === 'palette' ? (
                                            <div
                                                style={{
                                                    borderRadius: '12px',
                                                    background: 'var(--Backgrounds-Secoundary, #f5f7fa)',
                                                    width: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'var(--texts-icons-primary, #001965)',
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    padding: 12,
                                                    margin: '4px 0'
                                                }}
                                            >
                                                <>
                                                    {iconsMap[activeItem?.type]('#001965')}
                                                    <span style={{ marginLeft: 8, marginTop: '10px', fontWeight: 600 }}>
                                                        {activeItem.title}
                                                    </span>
                                                </>
                                            </div>
                                        ) : activeItem.origin === 'form' ? (
                                            <SortableFormItemDragOverlay item={activeItem} />
                                        ) : null
                                    ) : null}
                                </DragOverlay>
                            </div>
                        </div>
                        {/* Центральная дроп-зона */}

                        <div className="main__form form" ref={formScrollRef}>
                            <div
                                className={`form__static-area static-area ${
                                    selectedElement === 'static' ? 'selected' : ''
                                }`}
                                onClick={() => setSelectedElement('static')}
                            >
                                <div className="static-area__top-row">
                                    <div className="static-area__icon">
                                        <SupplierQuestionaryFormIcon />
                                    </div>
                                    <div className="static-area__subtext">
                                        Maximum Score for this form: <span>{commonScorePoints} points</span>
                                    </div>
                                </div>
                                <div className="static-area__title">{data?.title}</div>
                                <div className="static-area__description">
                                    {data?.description ? (
                                        <div
                                            className="static-area__description-color"
                                            style={{ whiteSpace: 'pre-wrap' }}
                                        >
                                            {data?.description}
                                        </div>
                                    ) : (
                                        'Form description (optional)'
                                    )}
                                </div>
                            </div>
                            {data?.sections?.map((section, index) => {
                                const fields = section?.parent_fields || [];
                                const showAll = expandedSections[section.id];
                                const visibleFields = showAll ? fields : fields.slice(0, 4);
                                return (
                                    <div
                                        className={`form__section section ${
                                            section?.id === selectedElement ? 'selected' : ''
                                        } ${sectionsError.some((id) => String(id) === String(section?.localId)) ? 'error' : ''}`}
                                        data-builder-section={String(section.localId)}
                                        key={section?.id}
                                    >
                                        {section?.id === selectedElement && data?.sections?.length > 1 && (
                                            <div className="section__arrows">
                                                {index === data?.sections?.length ? (
                                                    <></>
                                                ) : (
                                                    <>
                                                        {' '}
                                                        <Tooltip title="Move to bottom">
                                                            <div onClick={() => moveSectionToEnd(section?.localId)}>
                                                                <ArrowDownDown />
                                                            </div>
                                                        </Tooltip>
                                                        <Tooltip title="Move down">
                                                            <div onClick={() => moveSectionDown(section?.localId)}>
                                                                <ArrowDown />
                                                            </div>
                                                        </Tooltip>
                                                    </>
                                                )}

                                                {index === 0 ? (
                                                    <></>
                                                ) : (
                                                    <>
                                                        <Tooltip title="Move up">
                                                            <div onClick={() => moveSectionUp(section?.localId)}>
                                                                <ArrowDown style={{ transform: 'rotate(180deg)' }} />
                                                            </div>
                                                        </Tooltip>
                                                        <Tooltip title="Move to top">
                                                            <div onClick={() => moveSectionToStart(section.localId)}>
                                                                <ArrowDownDown
                                                                    style={{ transform: 'rotate(180deg)' }}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        <div
                                            className="section__header"
                                            onClick={() => setSelectedElement(section?.id)}
                                        >
                                            <div className="section__number">
                                                Section {index + 1} of {data?.sections?.length}
                                            </div>
                                            <div
                                                className={`section__title ${
                                                    sectionsError.some((id) => String(id) === String(section?.localId)) &&
                                                    !section?.title
                                                        ? 'error'
                                                        : section?.title
                                                        ? ''
                                                        : 'noTitle'
                                                } `}
                                            >
                                                {section?.title ? section?.title : 'Section Name'}
                                            </div>
                                            <div className="section__description">
                                                {section?.description ? (
                                                    <div
                                                        className="static-area__description-color"
                                                        style={{ whiteSpace: 'pre-wrap' }}
                                                    >
                                                        {section?.description}
                                                    </div>
                                                ) : (
                                                    'Section description (optional)'
                                                )}
                                            </div>
                                        </div>
                                        <div className="section__parent parent">
                                            <div className="parent__title">
                                                Questions from Supplier Profile Questionnaire:
                                            </div>
                                            {visibleFields.map((question: ParentFieldI, index: number) => (
                                                <div className="parent__item" key={index}>
                                                    <div className="parent__type">
                                                        {iconsMapForParentFields?.[question?.type]?.('#808CB2')}
                                                        {question?.type}
                                                    </div>
                                                    <div className="parent__label">{question.label}</div>
                                                </div>
                                            ))}
                                            {fields.length > 4 && (
                                                <div className="parent__btn" onClick={() => toggleSection(section.id)}>
                                                    {showAll ? 'Show Less' : `Show More`}{' '}
                                                    <img src={arrow} className={`${showAll ? 'showAll' : ''}`} />
                                                </div>
                                            )}
                                            <Divider />
                                        </div>
                                        <DroppableArea id={section.id} key={section.id} className="section__dropzone">
                                            {!!section?.fields?.length ? (
                                                <SortableContext
                                                    items={section.fields.map((item) => item.id)}
                                                    strategy={verticalListSortingStrategy}
                                                >
                                                    {section.fields.map((item) => (
                                                        <SortableFormItem
                                                            key={item.id}
                                                            id={item.id}
                                                            item={item}
                                                            selectedElement={selectedElement}
                                                            fieldsError={fieldsError}
                                                            onSelect={setSelectedElement}
                                                        />
                                                    ))}
                                                </SortableContext>
                                            ) : (
                                                <>
                                                    {fields?.length > 0 && (
                                                        <div className="section__subtitle">Score Card Questions</div>
                                                    )}
                                                    <div className="section__placeholder">
                                                        Please drag & drop question from Basic Elements
                                                    </div>
                                                </>
                                            )}
                                        </DroppableArea>
                                    </div>
                                );
                            })}

                            <div className="form__add-section" onClick={addNewSection}>
                                <AddSectionIcon /> Add Section
                            </div>
                        </div>
                        {/* Правая панель */}

                        <div className={`main__settings settings ${!selectedElement ? 'without' : ''}`}>
                            {selectedElement === 'static' && (
                                <>
                                    <div className="settings__title">Section Setting</div>
                                    <div className="settings__fields">
                                        <Form.Item>
                                            <FloatInput
                                                floatLabel={'Section Name'}
                                                className="input"
                                                value={data?.title}
                                                disabled
                                                maxLength={60}
                                            />
                                        </Form.Item>
                                        <Form.Item>
                                            <FloatTextArea
                                                floatLabel={'Section description (optional)'}
                                                className="input"
                                                hasError={() => false}
                                                value={data?.description}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                ) => {
                                                    setData((prev) => ({
                                                        ...prev,
                                                        description: e.target.value
                                                    }));
                                                }}
                                            />
                                        </Form.Item>
                                    </div>
                                </>
                            )}
                            {data?.sections?.find((elem) => elem.id === selectedElement) && (
                                <>
                                    <div className="settings__title">Section Setting</div>
                                    <div className="settings__fields">
                                        <Form.Item>
                                            <FloatInput
                                                floatLabel={'Section Name'}
                                                className="input"
                                                value={data?.sections?.find((el) => el.id === selectedElement)?.title}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                ) => {
                                                    setData((prev) => {
                                                        if (!prev) return prev;

                                                        const updatedSections = prev.sections.map((section) => {
                                                            if (section.id === selectedElement) {
                                                                return {
                                                                    ...section,
                                                                    title: e.target.value
                                                                };
                                                            }
                                                            return section;
                                                        });

                                                        return {
                                                            ...prev,
                                                            sections: updatedSections
                                                        };
                                                    });
                                                }}
                                                maxLength={60}
                                            />
                                        </Form.Item>
                                        <Form.Item>
                                            <FloatTextArea
                                                floatLabel={'Section description'}
                                                className="input"
                                                hasError={() => false}
                                                value={
                                                    data?.sections?.find((el) => el.id === selectedElement)?.description
                                                }
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                ) => {
                                                    setData((prev) => {
                                                        if (!prev) return prev;

                                                        const updatedSections = prev.sections.map((section) => {
                                                            if (section.id === selectedElement) {
                                                                return {
                                                                    ...section,
                                                                    description: e.target.value
                                                                };
                                                            }
                                                            return section;
                                                        });

                                                        return {
                                                            ...prev,
                                                            sections: updatedSections
                                                        };
                                                    });
                                                }}
                                            />
                                        </Form.Item>
                                        <Divider style={{ margin: '5px 0' }} />
                                        <div className="settings__row delete">
                                            <div className="settings__delete" onClick={() => setIsDeleteSection(true)}>
                                                <Delete fill="#E80A0F" />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {selectedElement &&
                                selectedElement !== 'static' &&
                                !data?.sections?.find((elem) => elem.id === selectedElement) && (
                                    <>
                                        <div className="settings__title">
                                            Field Setting
                                            <div className="settings__title-selected">
                                                {selectedElementData && iconsMap[selectedElementData?.type]('#808CB2')}
                                                {findItem()?.title}
                                            </div>
                                        </div>
                                        <div className="settings__fields">
                                            {findItem()?.type !== 'explanation-banner' ? (
                                                <>
                                                    <FloatInput
                                                        floatLabel={'Field label'}
                                                        className="input"
                                                        value={findItem()?.label}
                                                        onChange={(e) => updateFieldOrSection('label', e.target.value)}
                                                        maxLength={255}
                                                        error={
                                                            findItem()?.id &&
                                                            isFieldIdInErrors(findItem()!.id, fieldsError) &&
                                                            !findItem()?.label
                                                                ? true
                                                                : false
                                                        }
                                                    />
                                                    {findItem()?.id &&
                                                        isFieldIdInErrors(findItem()!.id, fieldsError) &&
                                                        !findItem()?.label && (
                                                            <div
                                                                className="error-text"
                                                                style={{ margin: '-10px 0 0 0' }}
                                                            >
                                                                This field is required
                                                            </div>
                                                        )}
                                                    <FloatTextArea
                                                        floatLabel={'Helper text'}
                                                        className="input"
                                                        hasError={() => false}
                                                        value={findItem()?.description}
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                        ) => updateFieldOrSection('description', e.target.value)}
                                                        maxLength={512}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <FloatInput
                                                        floatLabel={'Field label'}
                                                        className="input"
                                                        value={findItem()?.label}
                                                        onChange={(e) => updateFieldOrSection('label', e.target.value)}
                                                        maxLength={255}
                                                        error={
                                                            findItem()?.id &&
                                                            isFieldIdInErrors(findItem()!.id, fieldsError) &&
                                                            !findItem()?.label
                                                                ? true
                                                                : false
                                                        }
                                                    />
                                                    {findItem()?.id &&
                                                        isFieldIdInErrors(findItem()!.id, fieldsError) &&
                                                        !findItem()?.label && (
                                                            <div
                                                                className="error-text"
                                                                style={{ margin: '-10px 0 0 0' }}
                                                            >
                                                                This field is required
                                                            </div>
                                                        )}
                                                    <FloatTextArea
                                                        floatLabel={'Helper text'}
                                                        className="input"
                                                        hasError={() => false}
                                                        value={findItem()?.description}
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                                                        ) => updateFieldOrSection('description', e.target.value)}
                                                        maxLength={512}
                                                    />
                                                </>
                                            )}

                                            <div>
                                                <>
                                                    <Divider />
                                                    {/* options */}
                                                    {(() => {
                                                        const item = findItem();
                                                        if (!item || !item.options?.length) return null;

                                                        return (
                                                            <>
                                                                <div>
                                                                    {item.options.map((elem, index) => {
                                                                        return (
                                                                            <>
                                                                                <div className="settings__option">
                                                                                    <div className="settings__option-input">
                                                                                        <FloatInput
                                                                                            floatLabel={`Option ${
                                                                                                index + 1
                                                                                            }`}
                                                                                            className="input"
                                                                                            value={elem?.value}
                                                                                            onChange={(e) =>
                                                                                                updateOptionField(
                                                                                                    index,
                                                                                                    'value',
                                                                                                    e.target.value
                                                                                                )
                                                                                            }
                                                                                            error={
                                                                                                findItem()?.id &&
                                                                                                isFieldIdInErrors(
                                                                                                    findItem()!.id,
                                                                                                    fieldsError
                                                                                                ) &&
                                                                                                !elem?.value
                                                                                                    ? true
                                                                                                    : false
                                                                                            }
                                                                                            disabled={elem.disabled}
                                                                                            maxLength={255}
                                                                                        />
                                                                                        {findItem()?.id &&
                                                                                            isFieldIdInErrors(
                                                                                                findItem()!.id,
                                                                                                fieldsError
                                                                                            ) &&
                                                                                            !elem?.value && (
                                                                                                <div
                                                                                                    className="error-text"
                                                                                                    style={{
                                                                                                        margin: '4px 0 0 0'
                                                                                                    }}
                                                                                                >
                                                                                                    This field is
                                                                                                    required
                                                                                                </div>
                                                                                            )}
                                                                                    </div>
                                                                                    {findItem()?.settings?.find(
                                                                                        (elem) =>
                                                                                            elem.type ===
                                                                                            'rating-points'
                                                                                    )?.switchValue && (
                                                                                        <div className="settings__option-rating-input">
                                                                                            <FloatInput
                                                                                                floatLabel={'Rating'}
                                                                                                className="input small-input"
                                                                                                value={
                                                                                                    elem?.rating
                                                                                                        ? elem.rating
                                                                                                        : undefined
                                                                                                }
                                                                                                onChange={(e) =>
                                                                                                    updateOptionField(
                                                                                                        index,
                                                                                                        'rating',
                                                                                                        e.target.value
                                                                                                    )
                                                                                                }
                                                                                                maxLength={3}
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                                    {item &&
                                                                                        item?.options &&
                                                                                        item.options.length >
                                                                                            (findItem()?.type ===
                                                                                            'slider'
                                                                                                ? 2
                                                                                                : 1) && (
                                                                                            <div
                                                                                                className="settings__option-delete"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setIsDeleteOption(
                                                                                                        index + 1
                                                                                                    );
                                                                                                }}
                                                                                            >
                                                                                                <Delete fill="#808CB2" />
                                                                                            </div>
                                                                                        )}
                                                                                </div>
                                                                            </>
                                                                        );
                                                                    })}
                                                                </div>
                                                                <div className="settings__row">
                                                                    {(findItem()?.type !== 'slider' ||
                                                                        findItem()?.options?.length! < 10) && (
                                                                        <div
                                                                            className="settings__add"
                                                                            onClick={() => addOption()}
                                                                        >
                                                                            <img src={Plus} />
                                                                            Add an option
                                                                        </div>
                                                                    )}

                                                                    {findItem()?.settings?.map((setting) => {
                                                                        if (setting?.type === 'rating-points') {
                                                                            return (
                                                                                <div className="settings__rating">
                                                                                    <div className="settings__rating-row">
                                                                                        Score{' '}
                                                                                        <Switch
                                                                                            checked={
                                                                                                findItem()?.type ===
                                                                                                'slider'
                                                                                                    ? true
                                                                                                    : setting?.switchValue
                                                                                            }
                                                                                            size="small"
                                                                                            className="custom-switch"
                                                                                            onChange={() => {
                                                                                                if (
                                                                                                    findItem()?.type !==
                                                                                                    'slider'
                                                                                                ) {
                                                                                                    updateSettingField(
                                                                                                        'switchValue',
                                                                                                        !setting?.switchValue,
                                                                                                        setting?.type
                                                                                                    );
                                                                                                }
                                                                                            }}
                                                                                            disabled={
                                                                                                findItem()?.type ===
                                                                                                'slider'
                                                                                                    ? true
                                                                                                    : false
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                    <div className="settings__rating-score">
                                                                                        Max. Score:{' '}
                                                                                        <span>
                                                                                            {maxSwitchValue} points
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })}
                                                                </div>
                                                                <Divider />
                                                            </>
                                                        );
                                                    })()}

                                                    {/* Per Service Component */}
                                                    {findItem()?.settings?.map((setting) => {
                                                        if (setting.type === 'per_service_component') {
                                                            return (
                                                                <>
                                                                    <div className="settings__per-component">
                                                                        Per Service Component{' '}
                                                                        <Switch
                                                                            checked={setting?.switchValue}
                                                                            size="small"
                                                                            className="custom-switch"
                                                                            onChange={() =>
                                                                                updateSettingField(
                                                                                    'switchValue',
                                                                                    !setting?.switchValue,
                                                                                    setting?.type
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>

                                                                    {setting?.switchValue && (
                                                                        <div className="settings__related-components ">
                                                                            <Form>
                                                                                <Form.Item className="input-wrapper-multiselect">
                                                                                    <CustomMultiSelect
                                                                                        required={false}
                                                                                        floatLabel={
                                                                                            'Select related components'
                                                                                        }
                                                                                        err={false}
                                                                                        options={setting?.options
                                                                                            ?.filter(
                                                                                                (elem) => !elem.selected
                                                                                            )
                                                                                            .map((elem) => ({
                                                                                                name: elem.label,
                                                                                                id: elem.value
                                                                                            }))}
                                                                                        onChange={(
                                                                                            selectedValues: any
                                                                                        ) => {
                                                                                            if (
                                                                                                selectedValues?.name ===
                                                                                                'selectedAll'
                                                                                            ) {
                                                                                                selectAllOptions(
                                                                                                    setting?.type
                                                                                                );
                                                                                            } else {
                                                                                                updateOptionMultiSelectedInSetting(
                                                                                                    setting?.type,
                                                                                                    selectedValues?.id
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        hasError={() => false}
                                                                                        prefix={undefined}
                                                                                        value={
                                                                                            setting?.options
                                                                                                ?.filter(
                                                                                                    (option) =>
                                                                                                        option.selected
                                                                                                )
                                                                                                .map((option) => ({
                                                                                                    name: option.label,
                                                                                                    id: option.value
                                                                                                })) || []
                                                                                        }
                                                                                        deleteItem={(
                                                                                            selectedValues: any
                                                                                        ) => {
                                                                                            updateOptionMultiSelectedInSetting(
                                                                                                setting?.type,
                                                                                                selectedValues
                                                                                            );
                                                                                        }}
                                                                                        deleteAllItems={() =>
                                                                                            deselectAllOptions(
                                                                                                setting?.type
                                                                                            )
                                                                                        }
                                                                                        setTouched={() => {}}
                                                                                    />
                                                                                </Form.Item>
                                                                            </Form>
                                                                        </div>
                                                                    )}

                                                                    <Divider />
                                                                </>
                                                            );
                                                        }
                                                        if (setting.type === 'critical') {
                                                            return (
                                                                <>
                                                                    <div className="settings__per-component">
                                                                        <span>
                                                                            Needs Review{' '}
                                                                            <Tooltip title="The questions must be double-confirmed by the TE during the define phase.">
                                                                                <img src={i} />
                                                                            </Tooltip>
                                                                        </span>
                                                                        <Switch
                                                                            checked={setting?.switchValue}
                                                                            size="small"
                                                                            className="custom-switch"
                                                                            onChange={() =>
                                                                                updateSettingField(
                                                                                    'switchValue',
                                                                                    !setting?.switchValue,
                                                                                    setting?.type
                                                                                )
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <Divider />
                                                                </>
                                                            );
                                                        }
                                                        if (setting.type === 'file_upload_formats') {
                                                            return (
                                                                <div
                                                                    className="settings__question_category"
                                                                    key={setting.type}
                                                                >
                                                                    <div className="settings__question_category-title">
                                                                        Format
                                                                    </div>

                                                                    <div>
                                                                        {setting?.options?.map((elem) => {
                                                                            return (
                                                                                <div
                                                                                    key={elem.value}
                                                                                    className="settings__checkbox"
                                                                                >
                                                                                    <Checkbox
                                                                                        checked={elem.selected}
                                                                                        onChange={(value) => {
                                                                                            updateOptionMultiSelectedInSetting(
                                                                                                setting?.type,
                                                                                                elem.value
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                    {elem.label}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    })}

                                                    <div className="settings__row">
                                                        {/* required */}
                                                        <div className="settings__row-wrapper">
                                                            {findItem()?.settings?.map((setting) => {
                                                                if (setting?.type === 'required') {
                                                                    return (
                                                                        <div className="settings__required">
                                                                            Required
                                                                            <Switch
                                                                                checked={setting?.switchValue}
                                                                                size="small"
                                                                                className="custom-switch"
                                                                                onChange={() =>
                                                                                    updateSettingField(
                                                                                        'switchValue',
                                                                                        !setting?.switchValue,
                                                                                        setting?.type
                                                                                    )
                                                                                }
                                                                            />
                                                                        </div>
                                                                    );
                                                                }
                                                            })}
                                                        </div>
                                                        <div className="settings__row-wrapper">
                                                            <div
                                                                className="settings__delete icons"
                                                                onClick={() => setIsDeleteItem(true)}
                                                            >
                                                                <Delete fill="#E80A0F" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            </div>
                                        </div>
                                    </>
                                )}
                        </div>
                    </div>
                </DndContext>
            </div>
            <Modal
                className={''}
                centered
                open={!!isDeleteOption}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsDeleteOption(null)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={(e) => {
                                if (!isDeleteOption) return;
                                e.stopPropagation();
                                removeOption(isDeleteOption - 1);
                                setIsDeleteOption(null);
                            }}
                        >
                            Delete
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Delete option?
                    </div>
                    Are you sure you want to delete the
                    <b>
                        {isDeleteOption && findItem()?.options?.find((_, index) => index === isDeleteOption - 1)?.label}
                    </b>{' '}
                    from basic element - {findItem()?.title}?
                </div>
            </Modal>
            <Modal
                className={''}
                centered
                open={!!isDeleteItem}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsDeleteItem(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={(e) => {
                                if (!isDeleteItem) return;
                                e.stopPropagation();
                                removeItem();
                                setIsDeleteItem(false);
                                setSelectedElement(null);
                            }}
                        >
                            Delete
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Delete basic element?
                    </div>
                    Are you sure you want to delete the <b>{findItem()?.title}</b> basic element?
                </div>
            </Modal>
            <Modal
                className={''}
                centered
                open={!!isDeleteSection}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsDeleteSection(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={(e) => {
                                if (!isDeleteSection) return;
                                e.stopPropagation();
                                removeSection();
                                setIsDeleteSection(false);
                                setSelectedElement(null);
                            }}
                        >
                            Delete
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Delete section?
                    </div>
                    Are you sure you want to delete the{' '}
                    <b>{data?.sections?.find((elem) => elem.id === selectedElement)?.title ?? 'Section Name'}</b>{' '}
                    section?
                </div>
            </Modal>
            <Modal
                className={''}
                centered
                open={isCantPublishModal}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ width: '100%', margin: '30px 0 0 0' }}>
                        <Button
                            className={``}
                            style={{ width: '100%' }}
                            type="primary"
                            onClick={() => {
                                setIsCantPublishModal(false);
                            }}
                        >
                            Got it
                        </Button>
                    </div>
                }
            >
                <div className="modal-confirm">
                    <div className="modal-confirm__title" style={{ margin: '0 0 10px 0' }}>
                        Can’t publish document
                    </div>
                    Add a custom section to publish. The document must include at least one custom section with fields.
                </div>
            </Modal>
            {previewModal}
        </>
    );
};
